import { Chat } from "../../components/chat/Chat";
import { ChatSectionType, useChatSchedule } from "../../hooks/useChatSchedule";
import { Button, Flex, useToast } from "@chakra-ui/react";
import { useLayoutEffect, useState } from "react";
import { ChatSchedule } from "../../components/chatSchedule/ChatSchedule";
import { useIsSmallBreakpoint } from "../../utils/useBreakpoints";
import { BarRightIcon } from "../../components/icons/BarRightIcon";
import { useNavigate, useParams } from "react-router-dom";
import { useAccountData } from "../../hooks/useAccountData";
import { AblyWrapper } from "../../components/chat/components/AblyWrapper";
import { ChatProvider } from "../../components/chat/hooks/useChat";
import { useTopBarHeight } from "../../hooks/userTopBarHeight";

const globalChatId = `general-chat-${import.meta.env.DEV ? "dev" : "prod"}`;
export const globalChatEvent = {
  id: globalChatId,
  image: `${window.gc.global.assets_url}/static/images/web-ui/generalChatIcon.png`,
  title: "Hangout",
  subtitle: "Chill and talk comics",
  startTime: 0,
  endTime: Infinity,
};

export const SCHEDULE_PADDING_RIGHT = "60px";
export const SCHEDULE_WIDTH = "350px";
export const SCHEDULE_PADDING_Y = "40px";
export function ChatScreen() {
  const navigate = useNavigate();
  const toast = useToast();

  const accountData = useAccountData();
  const isSmall = useIsSmallBreakpoint();
  const topBarHeight = useTopBarHeight();
  const { uuid } = useParams();

  const { rooms, liveEvents, futureEvents, isLoading } = useChatSchedule();
  const [isScheduleOpen, setIsScheduleOpen] = useState(!isSmall);
  const [activeChatSection, setActiveChatSection] = useState<ChatSectionType>();

  useLayoutEffect(() => {
    const userData = accountData?.user;
    if (!isLoading) {
      const chatSections = [...rooms, ...liveEvents, ...futureEvents];
      if (uuid) {
        const newChatSection = chatSections.find((chatSection) => {
          if (!userData?.is_admin) {
            if (!userData && chatSection.requires_auth) {
              return false;
            }
            if (!userData?.gold && chatSection.requires_sub) {
              return false;
            }
          }
          return chatSection.uuid === uuid;
        });

        if (!newChatSection) {
          toast({
            title: `The event has ended, more to come. Stay tuned!`,
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          const defaultRoom = chatSections[0];
          navigate(`/chat/${defaultRoom?.uuid}`);
        } else {
          setActiveChatSection(newChatSection);
        }
      } else {
        const defaultRoom = chatSections[0];
        if (defaultRoom) {
          navigate(`/chat/${defaultRoom?.uuid}`);
        }
      }
    }
  }, [isLoading, uuid, accountData?.user]);

  const {
    ably_channel: chatChannel,
    display_name: chatName,
    image_small_url: chatIcon,
    start_time: chatStartTime,
    meta,
  } = activeChatSection || {};

  const {
    guest_ids: chatGuests,
    guest_icon_url: chatGuestIcon,
    pinned_message: pinnedMessage,
  } = meta?.config || {};

  return (
    <Flex
      gap="16px"
      height={`calc(100vh - ${topBarHeight.get()}px)`}
      width="100%"
      position="relative"
      margin="auto"
      alignItems="flex-start"
      justifyContent="center"
      py={isSmall ? "0px" : SCHEDULE_PADDING_Y}
      paddingLeft={isSmall ? "0px" : `calc(${SCHEDULE_PADDING_RIGHT} / 2)`}
      paddingRight={isSmall ? "0px" : SCHEDULE_PADDING_RIGHT}
      transition="padding-right 0.5s ease-in-out"
    >
      {activeChatSection && (
        <AblyWrapper channelId={chatChannel || ""}>
          <ChatProvider roomId={chatChannel || ""} guests={chatGuests || []}>
            <Chat
              title={chatName}
              icon={chatIcon}
              guests={chatGuests}
              guestsIcon={chatGuestIcon}
              eventStartTime={chatStartTime ?? "0"}
              pinnedMessage={pinnedMessage}
              flexDirection="column"
              height="100%"
              width={isSmall ? "100%" : `calc(100% - ${SCHEDULE_WIDTH})`}
              maxWidth="768px"
              flexGrow={1}
              button={
                <ToggleChatScheduleButton
                  isScheduleOpen={isScheduleOpen}
                  setIsScheduleOpen={setIsScheduleOpen}
                />
              }
            />
          </ChatProvider>
        </AblyWrapper>
      )}
      {rooms && futureEvents && liveEvents && (
        <ChatSchedule
          activeChatSection={activeChatSection}
          isOpen={isScheduleOpen}
          setIsOpen={setIsScheduleOpen}
          rooms={rooms}
          futureEvents={futureEvents}
          liveEvents={liveEvents}
        />
      )}
    </Flex>
  );
}

function ToggleChatScheduleButton({
  isScheduleOpen,
  setIsScheduleOpen,
}: {
  isScheduleOpen: boolean;
  setIsScheduleOpen: (isOpen: boolean) => void;
}) {
  return (
    <Button
      p="0px"
      onClick={() => {
        setIsScheduleOpen(!isScheduleOpen);
      }}
      background={isScheduleOpen ? undefined : "transparent"}
      aria-label="Toggle chat schedule"
    >
      <BarRightIcon boxSize="24px" color="dune.dune" />
    </Button>
  );
}
